import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { QUOTES_KEY, type Version, type VersionSummary } from './types';
import { DialogLayer } from '../ui/Dialog';

const shortDate = (iso: string) => new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit' }).format(new Date(iso));

/**
 * What the client may already have of the version; null when nothing (a draft, or a draft rejected by "Rezygnacja klienta").
 * The same rule as the server's `needsConfirm` (review M1): `confirm=true` is sent exactly when this is shown.
 */
function leftTheHouse(v: Pick<Version, 'number' | 'state' | 'sentAt'>): string | null {
  const when = v.sentAt ? ` ${shortDate(v.sentAt)}` : '';
  if (v.state === 'zaakceptowana') return `Wersja v${v.number} jest zaakceptowana przez klienta (wysłana${when}).`;
  if (v.state === 'wyslana') return `Wersja v${v.number} została wysłana do klienta${when} — klient może ją mieć.`;
  if (v.state === 'odrzucona') return v.sentAt ? `Wersja v${v.number} była wysłana do klienta${when} i odrzucona.` : null;
  return null;
}

/**
 * "Usuń wersję" (owner 2026-10-22): the version shown goes, the others stay; the only version takes the quote with it.
 * A version that left the house says so and is confirmed explicitly (`confirm=true`); one with invoices is refused by the
 * server (shown in place).
 */
export function DeleteVersionDialog({ version, versions, onDone, onClose }: { version: Version; versions: VersionSummary[]; onDone: () => void; onClose: () => void }) {
  const queryClient = useQueryClient();
  const only = versions.length <= 1;
  const others = versions.filter((x) => x.id !== version.id).map((x) => `v${x.number}`);
  const warning = leftTheHouse(version);
  const remove = useMutation({
    mutationFn: () => api<{ quoteDeleted: boolean }>(`/api/quotes/versions/${version.id}?version=${version.version}${warning ? '&confirm=true' : ''}`, { method: 'DELETE' }),
    onSuccess: () => onDone(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  const error = remove.error instanceof ApiError ? remove.error : remove.error ? new ApiError(0, 'unknown', 'Nie udało się usunąć') : null;
  const title = only ? 'Usunąć kosztorys?' : `Usunąć wersję v${version.number}?`;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-version-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-version-title">{title}</h2>
        {warning && <p className="q-delete-warning">{warning}</p>}
        <p>
          {only
            ? `To jedyna wersja (v${version.number}) — razem z nią zniknie cały kosztorys tego wydarzenia. W historii zmian zostaje wpis; potem można utworzyć nowy.`
            : `Zniknie tylko wersja v${version.number}. Pozostałe wersje (${others.join(', ')}) zostają bez zmian, sprzętówka też. Numer v${version.number} nie zostanie użyty ponownie.`}
        </p>
        {error?.status === 409 && <ConflictNotice onReload={onClose} />}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate()}>
            {only ? 'Usuń kosztorys' : `Usuń wersję v${version.number}`}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
